import React from 'react'
import { useIntl } from 'react-intl'
import _ from 'lodash'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import { Box, TableHead } from '@mui/material'
import { CheckCircleRounded } from '@mui/icons-material'
import TablePaginationActions from '../../../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import Unchecked from '../../../../../../assets/icons/UncheckedIcon.svg'
import EditIcon from '../../../../../../assets/icons/Edit/Edit.svg'
import ConfirmDelete from '../../../../../../components/dialogs/ConfirmDelete'
import { CoursePriceDetailsState } from '../../../../../../features/types'
import UnlockCourseDialog from '../../UnlockCourseDialog'

interface Props {
    prices: CoursePriceDetailsState[]
    pageNumber: number
    totalCount: number
    page: number
    rowsPerPage: number
    isFetching: boolean
    setPage: (page: number) => void
    setRowsPerPage: (rowsPerPage: number) => void
    handleOpen: (coursePrice: CoursePriceDetailsState) => void
    onActivate: (id: number) => void
    onDelete: (id: number) => void
}

const styles = {
    tableContainer: {
        border: 1,
        borderColor: 'grey.100',
        borderRadius: 2,
    },
    table: {
        minWidth: 500,
    },
    tableHead: {
        bgcolor: 'grey.100',
    },
    flexEnd: {
        display: 'flex',
        justifyContent: 'end',
    },
    iconButton: {
        background: '#f3f4f5',
        mr: 2,
    },
}

const PriceTable: React.FC<Props> = ({
    prices = [],
    pageNumber = 0,
    totalCount = 0,
    page = 0,
    rowsPerPage = 0,
    isFetching = false,
    setPage,
    setRowsPerPage,
    handleOpen,
    onActivate,
    onDelete,
}) => {
    const { formatMessage, locale } = useIntl()

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 1 ? Math.max(0, rowsPerPage - prices.length) : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(1)
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={styles.tableContainer}
                elevation={0}
            >
                <Table
                    sx={styles.table}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={styles.tableHead}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell>
                                {formatMessage({ id: 'nameSubject' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'subjects' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'promoCompaign' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'packageDiscount' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'status' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'optimalPrice' })}
                            </TableCell>

                            <TableCell
                                align="right"
                                width={12}
                            >
                                {formatMessage({ id: 'actions' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {prices.length
                            ? prices.map((row, index) => (
                                  <TableRow key={row.id}>
                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {(page - 1) * rowsPerPage + index + 1}
                                          .
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row[`title${_.capitalize(locale)}`]}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row.countSubjects ? (
                                              <CheckCircleRounded
                                                  color="primary"
                                                  sx={{ mr: 1 }}
                                              />
                                          ) : (
                                              <img src={Unchecked} />
                                          )}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row.countPromotions ? (
                                              <CheckCircleRounded
                                                  color="primary"
                                                  sx={{ mr: 1 }}
                                              />
                                          ) : (
                                              <img src={Unchecked} />
                                          )}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row.countDiscountPackages ? (
                                              <CheckCircleRounded
                                                  color="primary"
                                                  sx={{ mr: 1 }}
                                              />
                                          ) : (
                                              <img src={Unchecked} />
                                          )}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          <UnlockCourseDialog
                                              title={
                                                  row[
                                                      `title${_.capitalize(
                                                          locale,
                                                      )}`
                                                  ]
                                              }
                                              isActive={row.isActive}
                                              confirmAction={() =>
                                                  onActivate(row.id as number)
                                              }
                                          />
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row.optimalPrice}
                                      </TableCell>

                                      <TableCell align="right">
                                          <Box style={styles.flexEnd}>
                                              <IconButton
                                                  sx={styles.iconButton}
                                                  onClick={() =>
                                                      handleOpen(row)
                                                  }
                                              >
                                                  <img
                                                      src={EditIcon}
                                                      alt="edit"
                                                  />
                                              </IconButton>

                                              <ConfirmDelete
                                                  confirmAction={() =>
                                                      onDelete(row.id)
                                                  }
                                              />
                                          </Box>
                                      </TableCell>
                                  </TableRow>
                              ))
                            : !isFetching && (
                                  <TableRow>
                                      <TableCell
                                          component="th"
                                          scope="row"
                                          align="center"
                                          colSpan={8}
                                      >
                                          {formatMessage({ id: 'noData' })}
                                      </TableCell>
                                  </TableRow>
                              )}

                        {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={8} />
                            </TableRow>
                        )}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={8}
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={pageNumber}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default PriceTable
